import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { CheckCircle, Error, Home } from '@mui/icons-material';
import { activateShelter } from '@services/sheltersService';

const ShelterActivate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    parola: '',
    confirmParola: '',
    firstName: '',
    lastName: '',
    contactTelefon: '',
    acceptTermeni: false
  });

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Token de activare lipsă!');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Token de activare lipsă!');
      return;
    }

    if (formData.parola !== formData.confirmParola) {
      setError('Parolele nu coincid!');
      return;
    }

    if (!formData.acceptTermeni) {
      setError('Trebuie să accepți termenii și condițiile!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await activateShelter({
        token,
        parola: formData.parola,
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactTelefon: formData.contactTelefon,
        acceptTermeni: formData.acceptTermeni
      });
      
      if (response.success) {
        setSuccess('Contul adapostului a fost activat cu succes! Vei fi redirecționat la dashboard.');
        setTimeout(() => {
          navigate('/shelter/dashboard');
        }, 3000);
      } else {
        setError(response.error || 'Eroare la activarea contului');
      }
    } catch (err: any) {
      setError(err.message || 'Eroare la activarea contului. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Error color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Token de activare lipsă
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Link-ul de activare nu conține un token valid.
          </Typography>
          <Button variant="contained" onClick={handleGoHome} startIcon={<Home />}>
            Mergi la pagina principală
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Cont activat cu succes!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {success}
          </Typography>
          <Button variant="contained" onClick={handleGoHome} startIcon={<Home />}>
            Mergi la pagina principală
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Activează contul adapostului
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Completează formularul pentru a activa contul adapostului tău
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Prenume"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                fullWidth
                label="Nume"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                sx={{ flex: '1 1 200px' }}
              />
            </Box>
            <TextField
              fullWidth
              label="Telefon de contact"
              name="contactTelefon"
              value={formData.contactTelefon}
              onChange={handleInputChange}
              type="tel"
            />
            <TextField
              fullWidth
              label="Parolă"
              name="parola"
              type="password"
              value={formData.parola}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Confirmă parola"
              name="confirmParola"
              type="password"
              value={formData.confirmParola}
              onChange={handleInputChange}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="acceptTermeni"
                  checked={formData.acceptTermeni}
                  onChange={handleInputChange}
                />
              }
              label="Accept termenii și condițiile de utilizare"
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Se activează...' : 'Activează contul'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ShelterActivate;
